import dayjs from 'dayjs';
import { forwardRef } from 'react';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import i18n from '../../../../../../base-dictionary';
import { useFlowByPhaseTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { showEpicRelatedTasks } from '../../../../../../store/strategy-store/strategy-store.actions';
import { skyLight } from '../../../../../../styles/design-tokens';
import { Metric } from '../../../../strategy-v2.type';
import { ContentPlaceholder } from '../../../components/content-placeholder/content-placeholder';
import { LineChartDot } from '../../../components/line-chart/line-chart-dot';
import { hasArrayData } from '../../../initiative-performance.helpers';
import { useChartData } from '../../flow-by-phase-tab.hooks';
import type { Phase, PhaseMetadataItem } from '../../flow-by-phase-tab.type';
import { FlowByPhaseChartLegend } from './flow-by-phase-chart-legend';
import { FlowByPhaseChartTooltip } from './flow-by-phase-chart-tooltip';

export const FlowByPhaseChart = forwardRef<HTMLDivElement>((_, ref) => {
  const metric = useFlowByPhaseTabStore((state) => state.metric);
  const visiblePhases = useFlowByPhaseTabStore((state) => state.visiblePhases);
  const selectedEpic = useFlowByPhaseTabStore((state) => state.selectedEpic || undefined);

  const { chartData, currentDate, phaseMetadata } = useChartData();

  if (!currentDate || !hasArrayData(chartData)) {
    return <ContentPlaceholder icon="mobiledata_off" text={i18n.t('strategy.initiative_performance.no_data')} />;
  }

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <AreaChart data={chartData} margin={{ top: 20 }}>
          <defs>
            {phaseMetadata.map((phase: PhaseMetadataItem) => (
              <linearGradient key={`gradient-${phase.id}`} id={`color-${phase.id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="20%" stopColor={phase.color} stopOpacity={0.6} />
                <stop offset="80%" stopColor={phase.color} stopOpacity={0.3} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis
            dataKey="date"
            interval="preserveStartEnd"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            tickFormatter={(date: string) => dayjs(date).format("MMM, 'YY")}
            padding={{ right: 10 }}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: metric === Metric.Tasks ? 'Tasks' : 'Points',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              offset: 10,
            }}
          />
          <Tooltip content={<FlowByPhaseChartTooltip phaseMetadata={phaseMetadata} metric={metric} />} />
          {phaseMetadata
            .filter((phase: PhaseMetadataItem) => visiblePhases[phase.id])
            .map((phase: PhaseMetadataItem) => {
              const dataKey = metric === Metric.Tasks ? `tasks_${phase.id}` : `points_${phase.id}`;

              return (
                <Area
                  key={phase.id}
                  type="monotone"
                  dataKey={dataKey}
                  stackId="1"
                  stroke={phase.color}
                  fill={`url(#color-${phase.id})`}
                  fillOpacity={0.6}
                  strokeOpacity={1}
                  strokeWidth={1}
                  name={phase.label}
                  connectNulls={false}
                  animationDuration={0}
                  activeDot={
                    <LineChartDot
                      color={phase.color}
                      onClick={(_, event) => {
                        // Cast event to the proper type to access payload
                        const { payload } = event as unknown as { payload: { date: string } };

                        showEpicRelatedTasks({
                          epic: selectedEpic,
                          phase: phase.id as Phase,
                          date: dayjs(payload.date, 'YYYY-MM').toDate(),
                        });
                      }}
                    />
                  }
                />
              );
            })}
          <Legend content={<FlowByPhaseChartLegend />} verticalAlign="bottom" height={36} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
});

FlowByPhaseChart.displayName = 'FlowByPhaseChart';
